// Classic preference bar. Chips are matched by `sorter`, the only label that is
// the same for preferences read from Drupal and for those added in the session.
// `key` is the name the invest-preferences endpoint stores; `field` is the name
// the bar has always kept in memory (and reported) for a chip added by hand.
const chip = (key, name, sorter, field = key) => ({ key, field, name, sorter });

// Shown unless the account hides them. Hiding is bar-only: they stay in the
// stored invest preferences because the table view needs their columns.
export const LEGACY_BAR_DEFAULTS = [
  chip('instrument_number', 'Acciones', 'Acciones'),
  chip('in_price', 'Entrada', 'Entrada'),
  chip('out_price', 'Stop', 'Stop'),
  chip('support_price', 'Soporte', 'Soporte'),
  chip('support_date', 'Fecha de soporte', 'Fecha sop.')
];

// Optional chips are the stored invest preferences themselves, as before.
export const LEGACY_BAR_OPTIONAL = [
  chip('current_price', 'Valor actual', 'Valor act.', 'valor_actual'),
  chip('amount', 'Montante', 'Montante'),
  chip('in_currency', 'Divisa', 'Divisa', 'exchange_rate'),
  chip('tthh_profit', 'Rentabilidad', 'Rentab.'),
  chip('entry_distance', 'Distancia de entrada', 'Dist. Ent.'),
  chip('resistance', 'Resistencia', 'Resist.'),
  chip('anual_var', 'Variación anual', 'Var. Anual'),
  chip('wave_number', 'Número de onda', 'Nº onda'),
  chip('next_entry_lt', 'Siguiente LT', 'Sig. LT'),
  chip('first_trading_date', 'Primera fecha', '1ª fecha'),
  chip('risk_reward', 'Inversión/Riesgo', 'Inv/Riesgo'),
  chip('user_platinum_compatibility', 'Compatibilidad', 'Compat.'),
  chip('open_fee', 'Comisión de entrada', 'Comis. ent.'),
  chip('benefit', 'Beneficio', 'Beneficio'),
  chip('insured_benefit', 'Beneficio asegurado', 'Benef. Aseg.'),
  chip('in_date', 'Fecha de entrada', 'F. entrada')
];

// Bar-only chips: derived values with no invest preference or table column.
// Off until the account turns them on.
export const LEGACY_BAR_EXTRAS = [
  chip('support_age', 'Antigüedad del soporte', 'Antig. sop.')
];

// The chart header identifies the entry; the bar does not repeat it.
const IDENTITY = ['Ticker', 'T. entrada'];
const keysIn = (catalog, list) => catalog.map(item => item.key).filter(key => Array.isArray(list) && list.includes(key));
const defaultBySorter = sorter => LEGACY_BAR_DEFAULTS.find(item => item.sorter === sorter);

// Stored per account: default chips it hides and bar-only chips it shows.
export function normalizeLegacyBarState(state) {
  state = state && typeof state === 'object' ? state : {};
  return { hidden: keysIn(LEGACY_BAR_DEFAULTS, state.hidden), shown: keysIn(LEGACY_BAR_EXTRAS, state.shown) };
}

export function isLegacyBarChipHidden(sorter, preferences = [], hidden = []) {
  if (IDENTITY.includes(sorter) || !preferences.some(tool => tool.sorter === sorter)) return true;
  const base = defaultBySorter(sorter);
  return Boolean(base) && hidden.includes(base.key);
}

// Rows of the settings dialog: defaults first, then bar-only and optional chips.
export function legacyBarOptions(preferences = [], hidden = [], shown = []) {
  const has = sorter => preferences.some(tool => tool.sorter === sorter);
  return LEGACY_BAR_DEFAULTS.filter(item => has(item.sorter))
    .map(item => ({ ...item, base: true, checked: !hidden.includes(item.key) }))
    .concat(LEGACY_BAR_EXTRAS.map(item => ({ ...item, base: false, extra: true, checked: shown.includes(item.key) })))
    .concat(LEGACY_BAR_OPTIONAL.map(item => ({ ...item, base: false, checked: has(item.sorter) })));
}

// Whole payload for the invest-preferences endpoint, which keeps only the
// entries whose value equals their key. Built from what the bar holds so a
// chip read from Drupal is never dropped when another one changes.
export function legacyBarPreferencePayload(template, preferences = []) {
  const payload = {};
  Object.keys(template).forEach(key => { payload[key] = template[key] === 1 ? 1 : 0; });
  preferences.forEach(tool => {
    const known = LEGACY_BAR_DEFAULTS.concat(LEGACY_BAR_OPTIONAL).find(item => item.sorter === tool.sorter);
    const key = known ? known.key : tool.field;
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== 1) payload[key] = key;
  });
  return payload;
}
